import React, { useState } from "react";
import { FaInstagram, FaFacebook, FaCog, FaSpinner } from "react-icons/fa";
import { SocialAccountCard } from "./SocialAccountCard";
import { InstagramAccount } from "../types";
import { PrimaryActionButton } from "@/components/ui/PrimaryActionButton";
import { Button } from "@/components/ui/button";
import { DataStatusDot } from "./DataStatusDot";
import { useTranslation } from "react-i18next";
import { useCachedTier } from '@/features/account/hooks/useCachedTier';
import { AnalyticsUpgradeModal } from './AnalyticsUpgradeModal';

interface InstagramAccountCardProps {
  account: InstagramAccount;
  isLoadingInstagram: boolean;
  isIndexing: Record<string, boolean>;
  currentMessageIndex: Record<string, number>;
  indexingMessages: string[];
  onDisconnect: (connectionId?: string) => void;
  onTriggerIndex: (accountId: string, connectionId: string) => void;
  onReopenSelection?: () => Promise<void>;
}

export function InstagramAccountCard({
  account,
  isLoadingInstagram,
  isIndexing,
  currentMessageIndex,
  indexingMessages,
  onDisconnect,
  onTriggerIndex,
  onReopenSelection,
}: InstagramAccountCardProps) {
  const { t } = useTranslation();
  const accountId = account.id;
  const connectionId = account.connectionId;
  const isCurrentlyIndexing = isIndexing[accountId];
  const messageIndex = currentMessageIndex[accountId] || 0;
  const isConnectionOnly = account.isConnectionOnly;
  const isViaFacebook = account.connectionType === 'instagram_via_facebook';

  const { tier } = useCachedTier();
  const isPaidTier = tier !== null && tier.toLowerCase() !== 'free';
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleUpgrade = () => {
    setShowUpgradeModal(false);
    fetch('/api/stripe/create-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan: 'pro' }),
    })
      .then(async res => res.ok ? res.json() : null)
      .then(data => { if (data?.url) window.location.href = data.url; })
      .catch(err => console.error('[InstagramAccountCard] checkout error:', err));
  };

  const handleAnalytics = async () => {
    if (!isPaidTier) {
      setShowUpgradeModal(true);
      return;
    }
    setIsAnalyticsLoading(true);
    try {
      const response = await fetch('/api/analytics/unlock-analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectionId, username: account.username ?? account.name }),
      });
      if (!response.ok) {
        throw new Error(`Failed to unlock analytics: ${response.statusText}`);
      }
    } catch (error) {
      console.error('[InstagramAccountCard] Analytics request failed:', error);
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  const handleDisconnect = () => {
    onDisconnect(connectionId);
  };

  const getSubtitle = () => {
    const baseSubtitle = account.connectionType === 'instagram_direct'
      ? t('account_social.instagram_direct', 'Direct Connection')
      : t('account_social.instagram_via_facebook', 'Via Facebook');
    
    if (isConnectionOnly) {
      return `${baseSubtitle} (No Instagram Professional/ Creator Account)`;
    }
    
    return baseSubtitle;
  };

  const getIcon = () => {
    if (isConnectionOnly) {
      return <FaFacebook />;
    }
    return <FaInstagram />;
  };

  // Warning content for connection-only accounts
  const warningContent = isConnectionOnly ? (
    <div className="bg-orange-100 border border-orange-200 rounded-lg p-4">
      <p className="text-sm text-orange-800">
        <strong>{t('account_social.facebook_connected', 'Facebook connected')}</strong>{' '}
        {t(
          'account_social.no_instagram_professional',
          'but no Instagram Professional Account found. Make sure your Facebook page has an Instagram Professional Account linked to it.'
        )}
      </p>
    </div>
  ) : null;

  // Actions for regular Instagram accounts
  const actions = !isConnectionOnly ? (
    <div className="space-y-2">
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          className="w-full sm:flex-1 min-h-10 h-auto whitespace-normal text-center leading-tight py-2"
          variant="highlight"
          onClick={() => onTriggerIndex(accountId, connectionId)}
          disabled={isCurrentlyIndexing || isLoadingInstagram}
          aria-label={t('account_social.update_ai_aria')}
        >
          {isCurrentlyIndexing ? (
            <>
              <FaSpinner className="animate-spin" />
              {indexingMessages[messageIndex]}
            </>
          ) : (
            <>{t('account_social.update_ai')}</>
          )}
        </Button>
        <Button
          className="w-full sm:flex-1 min-h-10 h-auto whitespace-normal text-center leading-tight py-2"
          variant="secondary"
          onClick={handleAnalytics}
          disabled={isAnalyticsLoading}
          aria-label={t('account_social.unlock_analytics_aria')}
        >
          {isAnalyticsLoading ? (
            <>
              <FaSpinner className="animate-spin" />
              {t('account_social.unlock_analytics_loading')}
            </>
          ) : isPaidTier ? (
            t('account_social.unlock_analytics')
          ) : (
            t('account_social.unlock_analytics_locked')
          )}
        </Button>
      </div>

      {isViaFacebook && onReopenSelection && (
        <PrimaryActionButton
          onClick={onReopenSelection}
          className="w-full"
          hoverLight
        >
          {t('account_social.change_instagram_account', 'Change Instagram Account')}
        </PrimaryActionButton>
      )}
    </div>
  ) : null;

  // Ensure we have a valid profile picture URL without breaking proxied paths
  const profilePictureUrl = account.profile_picture_url
    ? (account.profile_picture_url.startsWith('http') || account.profile_picture_url.startsWith('/'))
      ? account.profile_picture_url
      : `https://${account.profile_picture_url.replace(/^https?:\/\//, '')}`
    : undefined;

  return (
    <>
      <SocialAccountCard
        title={account.name}
        username={account.username || undefined}
        profilePictureUrl={profilePictureUrl}
        icon={getIcon()}
        isLoading={isLoadingInstagram}
        variant={isConnectionOnly ? 'warning' : 'default'}
        onDisconnect={handleDisconnect}
        statusDot={<DataStatusDot connectionIds={[connectionId]} />}
      >
        {warningContent}
        {actions}
      </SocialAccountCard>
      <AnalyticsUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
      />
    </>
  );
}
